import { Code2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/hooks/useLanguage";

import zapierLogo from "@/assets/logos/zapier.svg";
import hubspotLogo from "@/assets/logos/hubspot.svg";
import pipedriveLogo from "@/assets/logos/pipedrive.svg";
import shopifyLogo from "@/assets/logos/shopify.svg";
import microsoft365Logo from "@/assets/logos/microsoft365.svg";

const integrations = [
  { name: "Zapier", logo: zapierLogo, bgColor: "bg-[#ff4a00]/10" },
  { name: "HubSpot", logo: hubspotLogo, bgColor: "bg-[#ff7a59]/10" },
  { name: "Pipedrive", logo: pipedriveLogo, bgColor: "bg-[#1b1b1b]/10" },
  { name: "Shopify", logo: shopifyLogo, bgColor: "bg-[#96bf48]/10" },
  { name: "Microsoft 365", logo: microsoft365Logo, bgColor: "bg-[#0078d4]/10" },
];

const IntegrationAutomationSection = () => {
  const { localizePath } = useLanguage();
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className={cn(
        "py-16 md:py-24 bg-secondary/30 transition-all duration-700 ease-out",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      )}
    >
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Badge variant="outline" className="mb-4 text-sm font-semibold text-primary uppercase tracking-wider">Integrationen</Badge>

          <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-6">
            Integration statt Insellösungen
          </h2>

          <p className="text-xl text-muted-foreground mb-12">
            AI Workflows & Automation sind darauf ausgelegt, sich nahtlos in bestehende Systemlandschaften einzufügen.
          </p>

          <div className="grid grid-cols-3 sm:grid-cols-5 gap-6 mb-8">
            {integrations.map((item, index) => (
              <div
                key={item.name}
                className={cn(
                  "flex flex-col items-center gap-3 transition-all",
                  isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                )}
                style={{ transitionDelay: isVisible ? `${index * 80}ms` : "0ms", transitionDuration: "500ms" }}
              >
                <div className={cn("w-16 h-16 rounded-2xl flex items-center justify-center p-3", item.bgColor)}>
                  <img src={item.logo} alt={`${item.name} Logo`} className="w-full h-full object-contain" />
                </div>
                <span className="text-sm font-medium text-foreground">{item.name}</span>
              </div>
            ))}
          </div>

          <div
            className={cn(
              "bg-card rounded-2xl p-6 border border-border inline-flex items-center gap-4 transition-all",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            )}
            style={{ transitionDelay: isVisible ? "500ms" : "0ms", transitionDuration: "500ms" }}
          >
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Code2 className="w-6 h-6 text-primary" />
            </div>
            <div className="text-left">
              <p className="font-semibold text-primary">REST API</p>
              <p className="text-sm text-muted-foreground">Individuelle Systemanbindung über eine flexible API-Schnittstelle.</p>
            </div>
          </div>

          <p className="text-muted-foreground mt-8">
            <Link to={localizePath("/integrationen")} className="text-chathero-mint font-semibold hover:underline">
              Alle Integrationen ansehen →
            </Link>
          </p>
        </div>
      </div>
    </section>
  );
};

export default IntegrationAutomationSection;
